import * as React from "react"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"

interface ComboboxOption {
  value: string
  label: string
  description?: string
  avatar?: string
  lastName?: string
}

interface ComboboxProps {
  id?: string
  value: string
  onValueChange: (value: string) => void
  options: ComboboxOption[]
  searchValue: string
  onSearchValueChange: (value: string) => void
  placeholder?: string
  renderOption?: (option: ComboboxOption) => React.ReactNode
  noOptionsMessage?: string
  className?: string
  dropdownClassName?: string;
  disabled?: boolean // <-- Add disabled prop
}

export const Combobox: React.FC<ComboboxProps> = ({
  id,
  value,
  onValueChange,
  options,
  searchValue,
  onSearchValueChange,
  placeholder,
  renderOption,
  noOptionsMessage = "No options found",
  className = "",
  dropdownClassName = "",
  disabled = false, // <-- default to false
}) => {
  const [open, setOpen] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)

  // Focus input field when dropdown opens
  React.useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0)
    } else if (!open) {
      setTimeout(() => onSearchValueChange(""), 100)
    }
  }, [open])

  const selectedOption = options.find((o) => o.value === value)

  return (
    <div className={`relative ${className}`} tabIndex={-1}>
      <div
        className={`w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm flex items-center ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-text'} ring-offset-background focus:outline-none focus:ring-2 focus:ring-primary relative`}
        onClick={() => { 
          if (!disabled) {
            setOpen(true)
            // Focus the input field when clicking anywhere in the combobox
            inputRef.current?.focus()
          }
        }}
        tabIndex={0}
        aria-disabled={disabled}
      >
        {selectedOption ? (
          <>
            {renderOption ? renderOption(selectedOption) : (
              <span className="text-gray-900 dark:text-gray-100">
                {selectedOption.label}
                {selectedOption.lastName ? ` ${selectedOption.lastName}` : ""}
              </span>
            )}
            {!disabled && (
              <button
                type="button"
                aria-label="Clear selection"
                className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none"
                onClick={e => {
                  e.stopPropagation();
                  onValueChange("");
                  setOpen(false);
                }}
              >
                <svg width="16" height="16" fill="none" viewBox="0 0 16 16"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4l8 8m0-8l-8 8"/></svg>
              </button>
            )}
          </>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">{placeholder}</span>
        )}
      </div>
      {open && !disabled && (
        <div
          className={`absolute z-10 mt-1 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg ${dropdownClassName}`}
          tabIndex={-1}
          onMouseDown={e => {
            // Prevent the default behavior which would blur the input
            e.preventDefault()
          }}
          onClick={e => {
            // Prevent click events from bubbling up and closing the dropdown
            e.stopPropagation()
          }}
        >
          <Command shouldFilter={false} className="p-0">
            <CommandInput
              ref={inputRef}
              value={searchValue}
              onValueChange={onSearchValueChange}
              placeholder={placeholder}
              className="border-none focus:ring-0 focus:outline-none bg-transparent px-3 py-2 text-sm"
              disabled={disabled}
              autoFocus
              onBlur={(e) => {
                // Only close if clicking outside the combobox completely
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setTimeout(() => setOpen(false), 100)
                }
              }}
            />
            <CommandList className="max-h-60 overflow-auto">
              <CommandEmpty>{noOptionsMessage}</CommandEmpty>
              <CommandGroup>
                {options.length === 0 ? null : options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => {
                      onValueChange(option.value)
                      setOpen(false)
                    }}
                    className="flex items-center gap-2 px-4 py-2 cursor-pointer"
                  >
                    {renderOption ? renderOption(option) : (
                      <span className="text-gray-900 dark:text-gray-100">
                        {option.label}
                        {option.lastName ? ` ${option.lastName}` : ""}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  )
}
